import { useAtom } from "jotai";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Interface/Button/Button";
import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import { usePaymentMode } from "@/hooks/usePaymentMode";
import { useGetAccountContact } from "@/services/api";
import { userAccountStateAtom } from "@/utils/atoms";

import {
  BILLING_INFO_FORM_ID,
  BillingInfoForm,
} from "./BillingInfoForm/BillingInfoForm";

interface Props {
  /**
   * Bills tab shows billing info for reference only - managing it stays
   * exclusive to the User account tab. See PaymentMethods.tsx.
   */
  readOnly?: boolean;
}

export const BillingInfo: FC<Props> = ({ readOnly = false }) => {
  const { data: billingInfo } = useGetAccountContact();
  const { t } = useTranslation();
  const { t: tCommon } = useTranslation("portal", {
    keyPrefix: "common.buttons",
  });
  const [isSavingBillingInfo, setIsSavingBillingInfo] = useState(false);
  const [userAccountState, setUserAccountState] = useAtom(userAccountStateAtom);

  const editingBillingInfo = userAccountState.editingBillingInfo;
  const { isInvoice } = usePaymentMode();

  // Mirrors PaymentMethods.tsx's own isEditingPaymentMethod: `editingPayment`
  // is a global atom that isn't reset on navigation, so a stale in-progress
  // edit from the User account tab must not hide this card's body/actions
  // on the read-only Bills tab. Invoice customers cannot change payment
  // method, so a leftover flag must not lock this card either.
  const isEditingPaymentMethod =
    userAccountState.editingPayment && !readOnly && !isInvoice;

  const setEditingBillingInfo = (isEditing: boolean) => {
    setUserAccountState((prev) => ({
      ...prev,
      editingBillingInfo: isEditing,
    }));
  };

  const FieldInfo = ({
    label,
    content,
  }: {
    label: string;
    content?: string;
  }) => (
    <div className="flex h-16 w-full flex-col justify-center gap-1 rounded bg-surface-quaternary px-5">
      <p className="text-xs leading-tight text-foreground-tertiary">{label}</p>
      <p className="truncate text-base font-semibold leading-tight text-foreground-primary">
        {content
          ? content
          : t("portal:user-account.paymentMethods.billingForm.notSpecified")}
      </p>
    </div>
  );

  return (
    <ProfileCard
      dataTestid="billing-info"
      title={t("portal:user-account.paymentMethods.billingForm.headline")}
      stickyHeader={editingBillingInfo && !readOnly}
      maxWidth="none"
      actions={
        !readOnly &&
        !isEditingPaymentMethod && (
          <>
            <Button
              dataTestid="billing-info-change-button"
              variant={editingBillingInfo ? "secondary" : "primary"}
              size="sm"
              disabled={!billingInfo}
              onClick={() => {
                setEditingBillingInfo(!editingBillingInfo);
              }}
            >
              {t(
                editingBillingInfo
                  ? `portal:user-account.paymentMethods.cancel`
                  : `portal:user-account.paymentMethods.change`,
              )}
            </Button>
            {editingBillingInfo && (
              <Button
                dataTestid="billing-info-save-button"
                variant="primary"
                size="sm"
                type="submit"
                form={BILLING_INFO_FORM_ID}
                disabled={isSavingBillingInfo}
              >
                {tCommon("save")}
              </Button>
            )}
          </>
        )
      }
    >
      {!isEditingPaymentMethod &&
        (editingBillingInfo && !readOnly ? (
          billingInfo && (
            <BillingInfoForm
              billingInfo={billingInfo}
              onSubmittingChange={setIsSavingBillingInfo}
            />
          )
        ) : (
          <>
            <FieldInfo
              label={t(
                "portal:user-account.paymentMethods.billingForm.emailAddress.label",
              )}
              content={billingInfo?.emailAddress}
            />
            <FieldInfo
              label={t(
                "portal:user-account.paymentMethods.billingForm.reference.label",
              )}
              content={billingInfo?.reference}
            />
            <FieldInfo
              label={t(
                "portal:user-account.paymentMethods.billingForm.note.label",
              )}
              content={billingInfo?.note}
            />
          </>
        ))}
    </ProfileCard>
  );
};
